import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, withRouter } from "react-router-dom";
import { useAppContext } from "../../AppContext";
import {
  faSyncAlt,
  faPlus,
  faBuilding,
  faBars,
  faGlobe,
  faChevronDown,
  faHeadset,
  faSignOutAlt,
  faEdit,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import Notifications from "./notifications/Notifications";
import { subscriptionStatus } from "../../services/data";
import moment from "moment";
import { logout } from "../../services/mainAxios";
import UseScreenSize from "../../utils/UseSizeScreen";
import SubscriptionModal from "./modals/SubscriptionModal";
import ChangeCompanyModal from "./modals/ChangeCompanyModal";
import SupportModal from "./modals/SupportModal";
import { useChatBotStore } from "../../store/ChatBotStore";
import BotAvatar from "../ChatBot/BotAvatar";

const Navbar = ({ history, location }) => {
  const {
    user: [user, setUser],
    selectedCompany: [selectedCompany, setSelectedCompany],
    company: [fullCompany],
    sideBarExpanded: [expanded, setExpanded],
    saveLanguage: saveLanguageSetting,
    saveSidebar,
  } = useAppContext();

  const navbar = useRef();
  const { isChatOpen, toggleChat } = useChatBotStore();
  const { t, i18n } = useTranslation("translations");
  const { width } = UseScreenSize();
  const isMobile = width <= 1024;

  // Dropdown states
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Language initialization
  useEffect(() => {
    const language = localStorage.getItem("language");
    i18n.changeLanguage(language === "alb" ? "alb" : "en");
  }, [i18n]);

  const saveLanguage = (val) => {
    localStorage.setItem("language", val);
  };

  const handleCompanyChange = (e) => {
    e.preventDefault();
    setShowCompanyModal(true);
  };

  const handleCompanySelect = (companyId) => {
    setSelectedCompany(companyId);
  };

  const hardReloadPage = () => {
    window.location.reload(true);
  };

  const toggleDropdown = (dropdown) => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
  };

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    saveLanguage(lang);
    saveLanguageSetting(lang);
  };

  const currentLanguage = i18n.language === "alb" ? "alb" : "en";
  const languageOptions = [
    {
      value: "alb",
      label: "Shqip",
      shortLabel: "AL",
      flagSrc: "/albania-icon-round.png",
      flagAlt: "Albanian",
    },
    {
      value: "en",
      label: "English",
      shortLabel: "EN",
      flagSrc: "/england-icon-round.png",
      flagAlt: "English",
    },
  ];
  const selectedLanguage = languageOptions.find(
    (option) => option.value === currentLanguage
  );

  const handleLogout = () => {
    logout({ setUser, request: true });
    history.push("/");
  };

  // Subscription data
  const activeSubscription = useMemo(() => {
    return fullCompany?.companySubscriptionViews?.find(
      (sub) => sub.status === subscriptionStatus.active
    );
  }, [fullCompany?.companySubscriptionViews]);

  const upcomingSubscription = useMemo(() => {
    return fullCompany?.companySubscriptionViews?.find(
      (sub) => sub.status === subscriptionStatus.upcoming
    );
  }, [fullCompany?.companySubscriptionViews]);

  const isExpanded = expanded?.show || expanded?.hover;

  const toggleSidebar = () => {
    const next = { show: !expanded?.show, hover: false };
    setExpanded(next);
    saveSidebar(next);
  };

  return (
    <>
      <nav
        className={`modern-navbar ${isExpanded ? 'modern-navbar--sidebar-expanded' : ''}`}
        ref={navbar}
      >
        {/* Left Section - Toggle button, Company selector */}
        <div className="modern-navbar__left">
          {!isExpanded && (
            <button
              className="modern-navbar__icon-btn"
              onClick={toggleSidebar}
              title={t("navbar.menu")}
            >
              <FontAwesomeIcon icon={faBars} />
            </button>
          )}

          {/* Company Selector - always visible */}
          <button
            className="modern-navbar__company"
            onClick={handleCompanyChange}
            title={fullCompany?.name}
          >
            <FontAwesomeIcon icon={faBuilding} />
            <span className="modern-navbar__company-name">{fullCompany?.name}</span>
          </button>

          {/* Subscription Indicator - desktop only */}
          {!isMobile && (
            <button
              className="modern-navbar__subscription"
              onClick={() => setShowSubscriptionModal(true)}
              title={t("subscription.subscription")}
            >
              <span className="modern-navbar__subscription-plan">
                {activeSubscription?.subscriptionName || t("subscription.subscription")}
              </span>
              {activeSubscription && (
                <span className={`modern-navbar__subscription-badge ${moment(activeSubscription.endDate).diff(moment(), 'days') < 10 ? 'modern-navbar__subscription-badge--danger' : ''}`}>
                  {moment(activeSubscription.endDate).diff(moment(), 'days')} {t("subscription.daysLeft")}
                </span>
              )}
            </button>
          )}
        </div>

        {/* Right Section - All navbar items */}
        <div className="modern-navbar__right" ref={dropdownRef}>
          {selectedCompany && (
            <button
              className={`modern-navbar__assistant ${isChatOpen ? "modern-navbar__assistant--active" : ""}`}
              onClick={toggleChat}
              title={t("chatbot.openChatAssistant")}
            >
              <BotAvatar size={30} disc="none" />
              <span className="modern-navbar__assistant-label">
                {t("chatbot.aiAssistant")}
              </span>
            </button>
          )}

          {!isMobile && (
            <div className="modern-navbar__language" title={t("navbar.language")}>
              <div className="modern-navbar__language-toggle" role="group" aria-label={t("navbar.language")}>
                {languageOptions.map((languageOption) => (
                  <button
                    key={languageOption.value}
                    type="button"
                    className={`modern-navbar__language-option ${currentLanguage === languageOption.value ? 'modern-navbar__language-option--active' : ''}`}
                    onClick={() => handleLanguageChange(languageOption.value)}
                    aria-pressed={currentLanguage === languageOption.value}
                    title={languageOption.label}
                  >
                    {currentLanguage === languageOption.value && (
                      <img
                        src={selectedLanguage?.flagSrc}
                        alt={selectedLanguage?.flagAlt}
                        className="modern-navbar__language-flag"
                      />
                    )}
                    <span>{languageOption.shortLabel}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {isMobile && (
            <div className="modern-navbar__dropdown">
              <button
                className="modern-navbar__icon-btn"
                onClick={() => toggleDropdown("language")}
                title={t("navbar.language")}
              >
                <img
                  src={selectedLanguage?.flagSrc}
                  alt={selectedLanguage?.flagAlt}
                  className="modern-navbar__language-flag"
                />
                <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: "10px", marginLeft: "4px" }} />
              </button>
              <div className={`modern-navbar__dropdown-menu ${activeDropdown === "language" ? "modern-navbar__dropdown-menu--visible" : ""}`}>
                {languageOptions.map((languageOption) => (
                  <button
                    key={languageOption.value}
                    className="modern-navbar__dropdown-item"
                    onClick={() => {
                      handleLanguageChange(languageOption.value);
                      setActiveDropdown(null);
                    }}
                  >
                    <img
                      src={languageOption.flagSrc}
                      alt={languageOption.flagAlt}
                      className="modern-navbar__language-flag modern-navbar__language-flag--menu"
                    />
                    <span>{languageOption.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Help/Support Button - always visible */}
          <button
            className="modern-navbar__icon-btn"
            onClick={() => setShowSupportModal(true)}
            title={t("navbar.help")}
          >
            <FontAwesomeIcon icon={faHeadset} />
          </button>

          {/* Notifications - always visible */}
          <Notifications />

          {/* User Dropdown - desktop only */}
          {!isMobile && (
            <div className="modern-navbar__dropdown">
              <button
                className="modern-navbar__user"
                onClick={() => toggleDropdown('user')}
              >
                <div className="modern-navbar__user-info">
                  <span className="modern-navbar__user-name">{user?.username}</span>
                  <span className="modern-navbar__user-role">{user?.role?.description}</span>
                </div>
                <FontAwesomeIcon icon={faChevronDown} className="modern-navbar__user-arrow" />
              </button>
              <div className={`modern-navbar__dropdown-menu modern-navbar__dropdown-menu--user ${activeDropdown === 'user' ? 'modern-navbar__dropdown-menu--visible' : ''}`}>
                <Link
                  to={"/users/edit/" + user?.id}
                  className="modern-navbar__dropdown-item"
                  onClick={() => setActiveDropdown(null)}
                >
                  <FontAwesomeIcon icon={faEdit} />
                  <span>{t("navbar.editProfile")}</span>
                </Link>
                <button
                  className="modern-navbar__dropdown-item"
                  onClick={() => {
                    setActiveDropdown(null);
                    hardReloadPage();
                  }}
                >
                  <FontAwesomeIcon icon={faSyncAlt} />
                  <span>{t("navbar.sync")}</span>
                </button>
                <div className="modern-navbar__dropdown-divider" />
                <button
                  className="modern-navbar__dropdown-item modern-navbar__dropdown-item--danger"
                  onClick={handleLogout}
                >
                  <FontAwesomeIcon icon={faSignOutAlt} />
                  <span>{t("navbar.logout")}</span>
                </button>
              </div>
            </div>
          )}

          {/* New Invoice Button - links to POS create when on POS pages */}
          {![3, 7].includes(user?.role?.id) && <Link
            to={location?.pathname?.startsWith("/pos-transactions") ? "/pos-transactions/create" : "/create-invoice"}
            className="modern-navbar__primary-btn"
          >
            <FontAwesomeIcon icon={faPlus} />
            <span>{t("navbar.newInvoice")}</span>
          </Link>}
        </div>
      </nav>

      {/* Change Company Modal - always available */}
      <ChangeCompanyModal
        isOpen={showCompanyModal}
        onClose={() => setShowCompanyModal(false)}
        companies={user?.companiesRights ?? []}
        selectedCompany={selectedCompany}
        onSelect={handleCompanySelect}
        t={t}
      />

      {/* Support Modal - always available */}
      <SupportModal
        isOpen={showSupportModal}
        onClose={() => setShowSupportModal(false)}
        t={t}
      />

      {/* Subscription Modal - desktop only */}
      {!isMobile && (
        <SubscriptionModal
          isOpen={showSubscriptionModal}
          onClose={() => setShowSubscriptionModal(false)}
          subscription={activeSubscription}
          upcomingSubscription={upcomingSubscription}
          t={t}
        />
      )}
    </>
  );
};

export default withRouter(Navbar);
